import {
  GltfDirectBuildError,
  GltfLowMemoryError,
  GltfLowMemoryUnsupportedError,
  MeshLoadCancelledError,
} from '@/plugins/mesh/errors'
import {
  attachGltfExtensionResults,
  createEmptyMeshLoadResult,
  createLowMemoryExtensionAccessorReader,
  disposeGltfExtensionResults,
  filterHandledGltfPrimitives,
  hasHandledGltfPrimitives,
} from '@/plugins/mesh/loaders/GltfExtensionIntegration'
import { applyGltfResourcePlan } from '@/plugins/mesh/loaders/gltfLowMemory/applyGltfResourcePlan'
import { DefaultGltfAccessorReader } from '@/plugins/mesh/loaders/gltfLowMemory/DefaultGltfAccessorReader'
import { DefaultGltfBatchPlanner } from '@/plugins/mesh/loaders/gltfLowMemory/DefaultGltfBatchPlanner'
import { DefaultGltfBufferSourceFactory } from '@/plugins/mesh/loaders/gltfLowMemory/DefaultGltfBufferSourceFactory'
import { DefaultGltfCoreMaterialFactory } from '@/plugins/mesh/loaders/gltfLowMemory/DefaultGltfCoreMaterialFactory'
import { DefaultGltfDirectGeometryBuilder } from '@/plugins/mesh/loaders/gltfLowMemory/DefaultGltfDirectGeometryBuilder'
import { DefaultGltfManifestReader } from '@/plugins/mesh/loaders/gltfLowMemory/DefaultGltfManifestReader'
import { DefaultGltfSceneAnalyzer } from '@/plugins/mesh/loaders/gltfLowMemory/DefaultGltfSceneAnalyzer'
import {
  collectGltfPreflightAsset,
  hasGltfResourceSnapshot,
  planGltfResources,
} from '@/plugins/mesh/loaders/gltfLowMemory/gltfResourcePreflight'
import type {
  GltfAccessorReader,
  GltfBatchPlanner,
  GltfBufferSource,
  GltfBufferSourceFactory,
  GltfCoreMaterialFactory,
  GltfDirectGeometryBuilder,
  GltfManifestReader,
  GltfSceneAnalyzer,
  GltfTaskScheduler,
} from '@/plugins/mesh/loaders/gltfLowMemory/interfaces'
import { resolveGltfLoadingOptions } from '@/plugins/mesh/loaders/gltfLowMemory/lowMemoryOptions'
import { MacrotaskGltfScheduler } from '@/plugins/mesh/loaders/gltfLowMemory/MacrotaskGltfScheduler'
import type {
  GltfBatchPlan,
  GltfManifestDocument,
  GltfMemoryPlan,
} from '@/plugins/mesh/loaders/gltfLowMemory/types'
import { createMeshProgressReporter, describeMeshSource } from '@/plugins/mesh/loaders/loaderUtils'
import type { MeshLoadContext } from '@/plugins/mesh/loaders/meshLoadContext'
import { resolveMeshOptimizationOptions } from '@/plugins/mesh/optimization/optimizationOptions'
import type {
  GltfLoaderAdoptedPath,
  GltfLoadingOptions,
  GltfLoadingOptionsInput,
  GltfLowMemoryReasonCode,
  GltfLowMemoryTimings,
  GltfMemoryReport,
  GltfTextureSizingReport,
  MeshLoadDiagnostics,
  MeshLoadRequest,
  MeshLoadResult,
  MeshOptimizationOptionsInput,
} from '@/plugins/mesh/types'
import type { GltfExtensionRegistry, GltfExtensionResult } from '@/runtime/GltfExtensionRegistry'
import { getLoggerManager } from '@/shared/logging'
import type { AssetLoader } from '@/shared/types/assets'

const log = getLoggerManager().getLogger('mesh:gltf')

/** 可替换的低内存 Loader 组件与每请求工厂。 */
export interface LowMemoryGltfLoaderDependencies {
  readonly manifestReader?: GltfManifestReader
  readonly sceneAnalyzer?: GltfSceneAnalyzer
  readonly batchPlanner?: GltfBatchPlanner
  readonly scheduler?: GltfTaskScheduler
  readonly bufferSourceFactory?: GltfBufferSourceFactory
  readonly accessorReader?: GltfAccessorReader
  readonly materialFactory?: GltfCoreMaterialFactory
  readonly geometryBuilder?: GltfDirectGeometryBuilder
}

/** 低内存 Loader 编排配置。 */
export interface LowMemoryGltfLoaderOptions {
  readonly loading?: GltfLoadingOptionsInput
  readonly optimization?: MeshOptimizationOptionsInput
  readonly dependencies?: LowMemoryGltfLoaderDependencies
  readonly extensionRegistry?: GltfExtensionRegistry
}

interface PhaseTimings {
  manifestMs: number
  planningMs: number
  buffersMs: number
  geometryMs: number
  materialsMs: number
  fallbackMs: number
}

const ZERO_MEMORY_REPORT: GltfMemoryReport = {
  sourceBytes: 0,
  finalGeometryBytes: 0,
  workspaceBytes: 0,
  estimatedTransientBytes: 0,
  largestBatchBytes: 0,
  actualAllocatedBytes: 0,
  peakRangeCacheBytes: 0,
  additionalDownloadedBytes: 0,
}

function disposeSources(sources: GltfBufferSource[]): void {
  for (const source of sources) {
    try {
      source.dispose()
    } catch {
      // 缓存和请求清理失败不覆盖原始加载错误。
    }
  }
  sources.length = 0
}

function isCancelled(error: unknown, signal: AbortSignal | undefined): boolean {
  return (
    signal?.aborted === true ||
    error instanceof MeshLoadCancelledError ||
    (error instanceof Error && error.name === 'AbortError')
  )
}

function getReasonCode(error: unknown): GltfLowMemoryReasonCode {
  return error instanceof GltfLowMemoryError ? error.code : 'direct-build-failed'
}

function createTimings(phases: PhaseTimings, startedAt: number): GltfLowMemoryTimings {
  return {
    ...phases,
    totalMs: getLoggerManager().now() - startedAt,
  }
}

function createSourceMemoryReport(
  plan: GltfMemoryPlan | undefined,
  sources: readonly GltfBufferSource[]
): GltfMemoryReport {
  const memory = plan ?? ZERO_MEMORY_REPORT
  return {
    ...memory,
    peakRangeCacheBytes: Math.max(
      memory.peakRangeCacheBytes,
      0,
      ...sources.map((source) => source.peakCacheBytes)
    ),
    additionalDownloadedBytes: sources.reduce(
      (total, source) => total + source.downloadedBytes,
      memory.additionalDownloadedBytes
    ),
  }
}

function withLoadingPath(
  result: MeshLoadResult,
  adoptedPath: GltfLoaderAdoptedPath,
  reasonCode: GltfLowMemoryReasonCode | undefined,
  memory: GltfMemoryReport,
  timings: GltfLowMemoryTimings,
  diagnostic?: string,
  textureSizing?: GltfTextureSizingReport
): MeshLoadResult {
  const diagnostics: MeshLoadDiagnostics = diagnostic
    ? {
        ...result.diagnostics,
        warnings: [...result.diagnostics.warnings, diagnostic],
      }
    : result.diagnostics
  return {
    ...result,
    diagnostics,
    report: {
      ...result.report,
      diagnostics,
      elapsedMs: timings.totalMs,
      adoptedPath,
      textureSizing,
      lowMemory: {
        adoptedPath,
        reasonCode,
        memory,
        timings,
        diagnostics: diagnostic ? [diagnostic] : [],
      },
    },
  }
}

function shouldAdoptAuto(plan: GltfBatchPlan, loading: GltfLoadingOptions): boolean {
  if (plan.entries.length === 0 || plan.memory.finalGeometryBytes === 0) return false
  const estimatedStandardPeak = plan.memory.sourceBytes + plan.memory.finalGeometryBytes * 2
  return (
    plan.memory.estimatedTransientBytes < estimatedStandardPeak ||
    plan.entries.some((entry) => entry.kind === 'instanced' || entry.kind === 'merged') ||
    loading.range.enabled
  )
}

/** standard/low-memory/auto 三态事务编排器。 */
export class LowMemoryGltfLoader implements AssetLoader<MeshLoadRequest, MeshLoadResult> {
  readonly format: 'gltf' | 'glb'
  readonly detectPriority: number

  private readonly manifestReader: GltfManifestReader
  private readonly sceneAnalyzer: GltfSceneAnalyzer
  private readonly batchPlanner: GltfBatchPlanner
  private readonly scheduler: GltfTaskScheduler

  constructor(
    private readonly standardLoader: AssetLoader<MeshLoadRequest, MeshLoadResult>,
    format: 'gltf' | 'glb',
    private readonly options: LowMemoryGltfLoaderOptions = {}
  ) {
    this.format = format
    this.detectPriority = standardLoader.detectPriority ?? 10
    this.manifestReader = options.dependencies?.manifestReader ?? new DefaultGltfManifestReader()
    this.sceneAnalyzer = options.dependencies?.sceneAnalyzer ?? new DefaultGltfSceneAnalyzer()
    this.batchPlanner = options.dependencies?.batchPlanner ?? new DefaultGltfBatchPlanner()
    this.scheduler = options.dependencies?.scheduler ?? new MacrotaskGltfScheduler()
  }

  canLoad(request: MeshLoadRequest): boolean {
    return this.standardLoader.canLoad?.(request) ?? false
  }

  async load(request: MeshLoadRequest, context: MeshLoadContext): Promise<MeshLoadResult> {
    let loading = resolveGltfLoadingOptions(this.options.loading, request.gltf)
    const source = describeMeshSource(request)
    const signal = context.signal ?? request.signal
    let directRequest: MeshLoadRequest = {
      ...request,
      format: this.format,
      gltf: loading,
      signal,
      onProgress: undefined,
    }
    const reportProgress = createMeshProgressReporter(request, context)
    const startedAt = getLoggerManager().now()
    const phases: PhaseTimings = {
      manifestMs: 0,
      planningMs: 0,
      buffersMs: 0,
      geometryMs: 0,
      materialsMs: 0,
      fallbackMs: 0,
    }
    log.debug(
      `event=mesh.gltf.load.start source=${source} format=${this.format} mode=${
        loading.mode
      } textureMaxSize=${String(loading.texture.maxSize)} textureConcurrency=${
        loading.texture.maxConcurrentDecodes
      }`
    )
    reportProgress(0)

    let document: GltfManifestDocument | undefined
    let textureSizing: GltfTextureSizingReport | undefined
    if (
      !context.resourcePlanApplied &&
      (loading.texture.maxSize === 'auto' || hasGltfResourceSnapshot(loading))
    ) {
      const manifestStartedAt = getLoggerManager().now()
      const asset = await collectGltfPreflightAsset(
        {
          ...directRequest,
          signal,
          optimization: resolveMeshOptimizationOptions(
            this.options.optimization,
            request.optimization
          ),
        },
        this.format,
        loading,
        this.options.dependencies?.manifestReader
          ? await this.manifestReader.read(directRequest, this.format, signal)
          : undefined,
        context.ktx2Service !== undefined,
        context.dracoService !== undefined
      )
      document = asset.document
      phases.manifestMs = getLoggerManager().now() - manifestStartedAt
      const resources = planGltfResources(
        [asset],
        loading,
        request.gltf?.budget?.maxTextureBytes ?? this.options.loading?.budget?.maxTextureBytes,
        1,
        phases.manifestMs,
        context.getLoadedMeshGpuBytes?.()
      )
      request.onPreflight?.(resources)
      const resolution = applyGltfResourcePlan(loading, resources, document.source)
      loading = resolution.loading
      textureSizing = resolution.report
      directRequest = {
        ...directRequest,
        data: directRequest.data ?? document.sourceData,
        gltf: loading,
      }
      reportProgress(0.12)
    }

    if (loading.mode === 'standard') {
      return this.loadStandard(
        directRequest,
        signal,
        reportProgress,
        0,
        startedAt,
        phases,
        'standard',
        'standard-requested',
        ZERO_MEMORY_REPORT,
        undefined,
        textureSizing,
        context
      )
    }

    const sources: GltfBufferSource[] = []
    let plan: GltfBatchPlan | undefined
    let extensionResults: ReadonlyMap<string, GltfExtensionResult> | undefined
    try {
      let sourceDocument: GltfManifestDocument
      if (!document || document.metadataOnly) {
        const manifestStartedAt = getLoggerManager().now()
        document = await this.manifestReader.read(directRequest, this.format, signal)
        phases.manifestMs += getLoggerManager().now() - manifestStartedAt
        reportProgress(0.12)
      }
      sourceDocument = document
      const hasHandledPrimitives = this.options.extensionRegistry
        ? hasHandledGltfPrimitives(this.options.extensionRegistry, sourceDocument)
        : false
      if (this.options.extensionRegistry && hasHandledPrimitives) {
        document = filterHandledGltfPrimitives(this.options.extensionRegistry, sourceDocument)
      }

      const planningStartedAt = getLoggerManager().now()
      const analysis = this.sceneAnalyzer.analyze(document, signal)
      const issue = analysis.compatibilityIssues[0]
      if (issue) {
        throw new GltfLowMemoryUnsupportedError(document.source, issue.code, issue.feature)
      }
      plan = this.batchPlanner.plan(document, analysis, {
        loading,
        optimization: resolveMeshOptimizationOptions(
          this.options.optimization,
          request.optimization
        ),
        signal,
      })
      phases.planningMs = getLoggerManager().now() - planningStartedAt
      reportProgress(0.22)

      if (loading.mode === 'auto' && !shouldAdoptAuto(plan, loading) && !hasHandledPrimitives) {
        const memory = createSourceMemoryReport(plan.memory, sources)
        disposeSources(sources)
        return this.loadStandard(
          directRequest,
          signal,
          reportProgress,
          0.22,
          startedAt,
          phases,
          'fallback',
          'auto-not-beneficial',
          memory,
          'auto 分析未发现可确认的低内存收益，采用 standard 路径',
          textureSizing,
          { ...context, onPart: undefined }
        )
      }

      const sourceFactory =
        this.options.dependencies?.bufferSourceFactory ??
        new DefaultGltfBufferSourceFactory(loading)
      const bufferCount = document.manifest.buffers?.length ?? 0
      const sourceProgress = Array.from({ length: bufferCount }, () => 0)
      for (let index = 0; index < bufferCount; index += 1) {
        sources.push(
          sourceFactory.create(document, index, {
            ...directRequest,
            onProgress: (progress) => {
              sourceProgress[index] = Math.max(sourceProgress[index], progress)
              const total =
                sourceProgress.reduce((sum, value) => sum + value, 0) /
                Math.max(1, sourceProgress.length)
              reportProgress(0.22 + total * 0.35)
            },
          })
        )
      }
      reportProgress(0.24)

      const materialFactory =
        this.options.dependencies?.materialFactory ??
        new DefaultGltfCoreMaterialFactory(directRequest)
      const accessorReader =
        this.options.dependencies?.accessorReader ??
        new DefaultGltfAccessorReader(this.scheduler, loading.taskYield)
      const geometryBuilder =
        this.options.dependencies?.geometryBuilder ??
        new DefaultGltfDirectGeometryBuilder(
          accessorReader,
          materialFactory,
          this.scheduler,
          loading.taskYield,
          loading.texture.maxConcurrentDecodes
        )
      const directDocument = document
      const directPlan = plan
      if (this.options.extensionRegistry && hasHandledPrimitives) {
        extensionResults = await this.options.extensionRegistry.process({
          document: sourceDocument.manifest,
          accessorReader: createLowMemoryExtensionAccessorReader(
            accessorReader,
            sourceDocument,
            sources
          ),
          signal,
        })
      }
      const geometryStartedAt = getLoggerManager().now()
      reportProgress(0.62)
      const result =
        directPlan.entries.length > 0
          ? await geometryBuilder.build(directDocument, directPlan, sources, directRequest)
          : createEmptyMeshLoadResult(this.format)
      const buildElapsedMs = getLoggerManager().now() - geometryStartedAt
      const buildTimings = result.report.lowMemory?.timings
      phases.buffersMs = buildTimings?.buffersMs ?? 0
      phases.materialsMs = buildTimings?.materialsMs ?? 0
      phases.geometryMs =
        buildTimings?.geometryMs ??
        Math.max(0, buildElapsedMs - phases.buffersMs - phases.materialsMs)
      reportProgress(0.94)
      const memory = {
        ...(result.report.lowMemory?.memory ?? plan.memory),
        peakRangeCacheBytes: Math.max(
          result.report.lowMemory?.memory.peakRangeCacheBytes ?? 0,
          ...sources.map((source) => source.peakCacheBytes)
        ),
        additionalDownloadedBytes: Math.max(
          result.report.lowMemory?.memory.additionalDownloadedBytes ?? 0,
          sources.reduce((total, source) => total + source.downloadedBytes, 0)
        ),
      }
      disposeSources(sources)
      reportProgress(1)
      const finalResult = withLoadingPath(
        result,
        result.report.adoptedPath ?? 'direct-shared',
        result.report.lowMemory?.reasonCode,
        memory,
        createTimings(phases, startedAt),
        undefined,
        textureSizing
      )
      log.debug(
        `event=mesh.gltf.load.complete source=${source} format=${this.format} path=${
          finalResult.report.adoptedPath ?? 'direct-shared'
        } elapsedMs=${finalResult.report.elapsedMs.toFixed(1)} manifestMs=${phases.manifestMs.toFixed(
          1
        )} planningMs=${phases.planningMs.toFixed(1)} buffersMs=${phases.buffersMs.toFixed(
          1
        )} materialsMs=${phases.materialsMs.toFixed(1)} geometryMs=${phases.geometryMs.toFixed(
          1
        )} entries=${plan.entries.length} primitives=${finalResult.report.stats.primitiveCount} textures=${
          directDocument.manifest.textures?.length ?? 0
        } downloadedBytes=${memory.additionalDownloadedBytes}`
      )
      return extensionResults
        ? attachGltfExtensionResults(finalResult, extensionResults)
        : finalResult
    } catch (rawError) {
      disposeGltfExtensionResults(extensionResults)
      const memory = createSourceMemoryReport(plan?.memory, sources)
      disposeSources(sources)
      if (isCancelled(rawError, signal)) {
        throw rawError instanceof MeshLoadCancelledError
          ? rawError
          : new MeshLoadCancelledError(request.url ?? '(内存 glTF)', {
              cause: signal?.reason ?? rawError,
            })
      }
      const error =
        rawError instanceof GltfLowMemoryError
          ? rawError
          : new GltfDirectBuildError(
              request.url ?? '(内存 glTF)',
              rawError instanceof Error ? rawError.message : String(rawError),
              rawError
            )
      if (error.code === 'budget-texture-exceeded') throw error
      const canFallback = loading.mode === 'auto' || loading.unsupportedMode === 'fallback'
      if (!canFallback) throw error
      log.debug(
        `event=mesh.gltf.fallback source=${source} progress=0.24 reasonCode=${getReasonCode(
          error
        )} reason=${error.message}`
      )
      return this.loadStandard(
        directRequest,
        signal,
        reportProgress,
        0.24,
        startedAt,
        phases,
        'fallback',
        getReasonCode(error),
        memory,
        `低内存路径未采用，原因码: ${getReasonCode(error)}，原因: ${error.message}`,
        textureSizing,
        { ...context, onPart: undefined }
      )
    }
  }

  private async loadStandard(
    request: MeshLoadRequest,
    signal: AbortSignal | undefined,
    reportProgress: (progress: number) => void,
    progressStart: number,
    startedAt: number,
    phases: PhaseTimings,
    adoptedPath: 'standard' | 'fallback',
    reasonCode: GltfLowMemoryReasonCode,
    memory: GltfMemoryReport,
    diagnostic?: string,
    textureSizing?: GltfTextureSizingReport,
    context?: MeshLoadContext
  ): Promise<MeshLoadResult> {
    const fallbackStartedAt = getLoggerManager().now()
    const result = await this.standardLoader.load(
      {
        ...request,
        gltf: { ...request.gltf, mode: 'standard' },
        onProgress: undefined,
      },
      {
        signal,
        ...context,
        onProgress: (progress) => reportProgress(progressStart + progress * (1 - progressStart)),
      }
    )
    if (adoptedPath === 'fallback') {
      phases.fallbackMs = getLoggerManager().now() - fallbackStartedAt
    }
    reportProgress(1)
    const finalResult = withLoadingPath(
      result,
      adoptedPath,
      reasonCode,
      memory,
      createTimings(phases, startedAt),
      diagnostic,
      textureSizing
    )
    log.debug(
      `event=mesh.gltf.load.complete source=${describeMeshSource(request)} format=${this.format} path=${
        finalResult.report.adoptedPath ?? adoptedPath
      } elapsedMs=${finalResult.report.elapsedMs.toFixed(1)} manifestMs=${phases.manifestMs.toFixed(
        1
      )} fallbackMs=${phases.fallbackMs.toFixed(1)}`
    )
    return finalResult
  }
}
